import React, { useEffect } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';


import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Flex } from '@atlaskit/primitives/compiled';
import { usePlatformInstrumentation } from '@atlassian/platform-analytics';

import { PlaylistErrorIllustration } from '../assets/PlaylistErrorIllustration';

const messages = defineMessages({
	errorSuggestedHeading: {
		id: 'ai-audio.suggested.error.heading',
		defaultMessage: 'Hmm, something went wrong',
		description: 'Heading shown when playlist suggested items fail to load',
	},
	errorSuggestedMessage: {
		id: 'ai-audio.suggested.error.message',
		defaultMessage: 'Failed to load suggestions',
		description: 'Default error message shown when playlist suggested items fail to load',
	},
	tryAgainButton: {
		id: 'ai-audio.suggested.error.try-again',
		defaultMessage: 'Try again',
		description: 'Button text to retry loading playlist suggested items',
	},
});

const styles = cssMap({
	container: {
		flex: 1,
	},
	content: {
		width: '290px',
		textAlign: 'center',
	},
});

interface SuggestedErrorViewProps {
	onRetry?: () => void;
}

export const SuggestedErrorView = ({ onRetry }: SuggestedErrorViewProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendTrackEvent } = usePlatformInstrumentation();

	useEffect(() => {
		sendTrackEvent({
			eventType: 'track',
			action: 'shown',
			actionSubject: 'playlistSuggestionsError',
			source: 'playlist',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Flex
			xcss={styles.container}
			justifyContent="center"
			alignItems="center"
			rowGap="space.200"
			direction="column"
		>
			<PlaylistErrorIllustration />
			<Flex xcss={styles.content} alignItems="center" rowGap="space.150" direction="column">
				<Heading size="small" as="h3">
					{formatMessage(messages.errorSuggestedHeading)}
				</Heading>
			</Flex>
			{onRetry && (
				<Button appearance="default" onClick={onRetry}>
					{formatMessage(messages.tryAgainButton)}
				</Button>
			)}
		</Flex>
	);
};
